import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useTranslation } from 'react-i18next';
import StatusBadge from "../StatusBadge";
import { SharedPagination } from "../shared";
import { LoadingOverlay } from "../LoadingOverlay";

export default function LocationDetailedView({
  paginatedApplications,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  tableLoading,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className="bg-white border border-stone-200 overflow-hidden">
      {/* Top Pagination Controls */}
      <SharedPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
        variant="location"
        position="top"
      />
      
      {/* Table with Loading Overlay */}
      <div className="relative overflow-x-auto">
        {tableLoading && <LoadingOverlay fullPage={false} />}
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.slNo')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.applicationId')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.surveyNumber')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.farmer')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('newApplication.village')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('newApplication.hobli')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('newApplication.taluk')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.status')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.date')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-emerald-700 uppercase">{t('table.action')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {paginatedApplications.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-4 py-8 text-center text-slate-500">
                  {t('locationReports.noApplications')}
                </td>
              </tr>
            ) : (
              paginatedApplications.map((app, index) => (
                <tr key={app.id} className="hover:bg-stone-50 transition-colors" data-testid={`app-row-${app.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {((currentPage - 1) * pageSize) + index + 1}
                  </td>
                  <td className="px-4 py-3">
                    {app.application_id ? (
                      <div>
                        <span className="text-xs font-semibold text-blue-600 font-mono block">{app.application_id.substring(0, 7)}</span>
                        <span className="text-sm font-bold text-blue-700 font-mono">{app.application_id.substring(8)}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-slate-900">{app.land_survey_number}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{app.farmer_name}</p>
                    <p className="text-xs text-slate-500">{app.farmer_email}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-900">{app.village || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{app.hobli || "-"}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{app.taluk || "-"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={app.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {format(new Date(app.created_at), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/application/${app.id}`)}
                      className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                      data-testid={`view-app-${app.id}`}
                    >
                      {t('table.viewDetails')}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Bottom Pagination Controls */}
      {totalCount > pageSize && (
        <SharedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={onPageChange}
          variant="location"
          position="bottom"
        />
      )}
    </Card>
  );
}
